import { Component, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { AranDrawerModalService } from '@aran-client/aran-web-ui-toolkit';
import { Store } from '@ngxs/store';
import { Observable, switchMap, tap } from 'rxjs';
import { PagingConfig } from 'src/app/core/models/paging-config.model';
import { FilterService } from 'src/app/services/filter/filter.service';
import { routeMetaData } from 'src/app/shared/constants/route-metadata.constant';
import { statusStylesWithColor } from 'src/app/shared/constants/status-color.constant';
import { RequestStatus } from 'src/app/shared/enums/status.enum';
import { StoreHelperService } from 'src/app/shared/services/store-helper/store-helper.service';
import * as requestsTypes from 'src/app/shared/types/requests.types';
import { getRouteParamAsNumber } from 'src/app/shared/utils/route.utils';
import { RequestsAction } from 'src/app/store/requests/requests.action';
import { RequestsState } from 'src/app/store/requests/requests.state';

@Component({
  selector: 'app-request-detail',
  templateUrl: './request-detail.component.html',
  styleUrls: ['./request-detail.component.scss'],
})
export class RequestDetailComponent implements OnInit {
  @ViewChild('updateRequest') updateRequest!: TemplateRef<any>;

  routeMetaData = routeMetaData;

  currentTemplate!: TemplateRef<any>;
  modalTitle: string = '';
  selectedIndexes: number[] = [];

  currentPage: number = 1;
  itemsPerPage: number = 10;
  maxSize: number = 5;
  totalItems: number = 0;
  pageSizeOptions: number[] = [5, 10, 25, 100];
  pagingConfig: { [key: string]: PagingConfig } = {};
  getAirportKey = (index: number) => `AirportHeliport-${index}`;
  getFirKey = (index: number) => `Airspace-${index}`;
  getScenarioKey = (index: number) => `Scenario-${index}`;

  filters: { [key: string]: string } = {
    Scenario: '',
    AirportHeliport: '',
    Airspace: '',
  };

  public selectedElementId!: number;

  menuItems: any[] = [];

  requestDetail$!: Observable<requestsTypes.RequestsDetail | null>;
  requestDetail!: requestsTypes.RequestsDetail | null;
  isLoading$: Observable<boolean> = this.store.select(
    RequestsState.getIsLoading()
  );

  constructor(
    private store: Store,
    private route: ActivatedRoute,
    private router: Router,
    private aranDrawerModalService: AranDrawerModalService,
    private filterService: FilterService,
    private storeHelperService: StoreHelperService
  ) { }

  ngOnInit(): void {
    this.getDetails();
  }

  buildMenuItems() {
    this.menuItems = [
      {
        isVisible: this.requestDetail?.status === 'Pending',
        name: 'Edit',
        icon: 'edit',
        iconType: 'material',
        type: 'action',
        action: () => this.update(),
      },
      {
        isVisible: this.requestDetail?.status === 'Pending',
        name: 'Delete',
        icon: 'delete',
        iconType: 'material',
        type: 'action',
        redHoverColor:true,
        action: () => this.delete(this.selectedElementId),
      },
    ];
  }

  getDetails() {
    getRouteParamAsNumber(this.route, 'id')
      .pipe(
        tap((id) => {
          this.selectedElementId = id;
        }),
        switchMap((id) =>
          this.storeHelperService.getAllDispatchAndSelect(
            new RequestsAction.GetRequestDetail(id),
            RequestsState.getRequestDetail
          )
        ),
        tap((data) => {
          this.selectedIndexes = data?.topics.map(() => 0) ?? [];
          this.requestDetail = data;
          this.assignPagingConfig(data);
          this.buildMenuItems();
        })
      )
      .subscribe();
  }

  assignPagingConfig(data: requestsTypes.RequestsDetail | null) {
    data?.topics?.forEach((topic, index) => {
      const airportKey = this.getAirportKey(index);
      const firKey = this.getFirKey(index);
      const scenarioKey = this.getScenarioKey(index);

      this.filters[airportKey] = '';
      this.filters[firKey] = '';
      this.filters[scenarioKey] = '';

      this.pagingConfig[airportKey] = {
        id: airportKey,
        itemsPerPage: this.itemsPerPage,
        currentPage: this.currentPage,
        totalItems:
          topic.topicAreas?.filter((area) =>
            ['Aerodrome', 'Heliport'].includes(area.type)
          ).length || 0,
        footerTablePagination: true,
      };
      this.filterService.setPagingConfigValues(airportKey, this.pagingConfig[airportKey]);
      this.pagingConfig[airportKey] = this.filterService.getPagingConfigValues(airportKey);

      this.pagingConfig[firKey] = {
        id: firKey,
        itemsPerPage: this.itemsPerPage,
        currentPage: this.currentPage,
        totalItems:
          topic.topicAreas?.filter((area) => area.type === 'Airspace').length || 0,
        footerTablePagination: true,
      };
      this.filterService.setPagingConfigValues(firKey, this.pagingConfig[firKey]);
      this.pagingConfig[firKey] = this.filterService.getPagingConfigValues(firKey);

      const isBothCustom =
        topic.oldSubTopicSelectedOption === 'Custom' &&
        topic.newSubTopicSelectedOption === 'Custom';

      let filteredSubTopics = topic.subTopics ?? [];

      if (!isBothCustom) {
        filteredSubTopics = filteredSubTopics.filter(
          (subTopic) => subTopic.status === 'Current'
        );
      }

      this.pagingConfig[scenarioKey] = {
        id: scenarioKey,
        itemsPerPage: this.itemsPerPage,
        currentPage: this.currentPage,
        totalItems: filteredSubTopics.length,
        footerTablePagination: true,
      };
      this.filterService.setPagingConfigValues(scenarioKey, this.pagingConfig[scenarioKey]);
      this.pagingConfig[scenarioKey] = this.filterService.getPagingConfigValues(scenarioKey);
    });
  }



  update = () => {
    this.modalTitle = 'Update Service Request';
    this.currentTemplate = this.updateRequest;
    this.aranDrawerModalService.show();
  };

  delete(id: number): void {
    this.storeHelperService
      .getAllDispatchAndSelect(
        new RequestsAction.DeleteRequest(id)
      )
      .pipe(
        tap(() => {
          this.aranDrawerModalService.hide();
          this.router.navigate([this.routeMetaData.requests.link]);
        })
      )
      .subscribe();
  }

  onTabChanged(newIndex: number, itemIndex: number) {
    this.selectedIndexes[itemIndex] = newIndex;
  }

  getStatusColor(status: string | null): string {
    return statusStylesWithColor[status as RequestStatus] || 'black';
  }

  onHandlePaginate(event: number, tableKey: string) {
    this.filterService.updatePagingConfigValue(tableKey, 'currentPage', event);
  }

  onHandlePageSize(newSize: number, tableKey: string) {
    this.filterService.updatePagingConfigValue(tableKey, 'currentPage', 1);
    this.filterService.updatePagingConfigValue(
      tableKey,
      'itemsPerPage',
      newSize
    );
  }

  applyFilter(key: string): void {
    this.filterService.updatePagingConfigValue(key, 'currentPage', 1);

    this.filterService.updatePagingConfigValue(
      key,
      'totalItems',
      this.totalItems
    );
  }

  resetFilter(key: string) {
    this.filters[key] = '';
    this.filterService.updatePagingConfigValue(key, 'currentPage', 1);
  }

  hasAddOrRemoveStatus(data: any[]): boolean {
    return data?.some(
      (item) => item.status === 'Add' || item.status === 'Remove'
    );
  }

  getBadgeCount(list: any[]): string {
    if (!Array.isArray(list)) {
      return '0';
    }

    const count = list.filter(
      (topic) => topic.status === 'Add' || topic.status === 'Remove'
    ).length;

    return count.toString();
  }
}
